import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Icon from 'components/priority/Icon/Icon'
import { groupFontFamilies } from '@/utils/styling/fontRegions'
import {
  canonicalFontName,
  editorTextBoxFontRegionLabel,
  fontDisplayLabel,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import * as S from './EditorTextBoxToolbar.styled'

type DataAttrs = Record<string, string | boolean>

type FontMenuGroup = {
  key: string
  label: string
  families: readonly string[]
  itemAttr: string
  sectionAttr: DataAttrs
  triggerAttr: DataAttrs
  expandedAttr: DataAttrs
}

type ActiveFlyout = FontMenuGroup & { rect: DOMRect }

type FontItemProps = {
  family: string
  attr: string
  current?: boolean
  onPick: (family: string) => void
}

type EditorTextBoxFontMenuProps = {
  families: readonly string[]
  onPick: (family: string) => void
  usedFonts?: readonly string[]
  listProps?: DataAttrs
  flyout?: boolean
  /** The family the selection resolves to; its row carries the check. */
  currentFamily?: string
}

/** The tick ending a list's current row: the font and role menus share it. */
export const MenuTick: FC = () => (
  <S.MenuCheck aria-hidden>
    <Icon icon="check" fill="#2a8acd" width="12px" height="8px" />
  </S.MenuCheck>
)

const FontItem: FC<FontItemProps> = ({ family, attr, current, onPick }) => (
  <S.MenuItem
    type="button"
    {...{ [attr]: family }}
    {...(current ? { 'data-editor-text-font-current': true } : {})}
    style={{ fontFamily: `'${family}'` }}
    onMouseDown={(e) => e.preventDefault()}
    onClick={() => onPick(family)}
  >
    {fontDisplayLabel(family)}
    {current && <MenuTick />}
  </S.MenuItem>
)

const FLYOUT_WIDTH = 240

/**
 * Nested groups stay inline disclosures unless `flyout` is set: a flyout
 * portals to the document body, and a host modal's outside-click dismiss
 * reads a body portal as outside itself and closes.
 */
const EditorTextBoxFontMenu: FC<EditorTextBoxFontMenuProps> = ({
  families,
  onPick,
  usedFonts = [],
  listProps,
  flyout = false,
  currentFamily,
}) => {
  const { t } = useTranslation()
  const { main, regions } = useMemo(
    () => groupFontFamilies(families),
    [families],
  )
  const currentCanonical = currentFamily
    ? (canonicalFontName(currentFamily) ?? currentFamily)
    : undefined
  const isCurrent = (family: string) =>
    currentCanonical !== undefined &&
    (canonicalFontName(family) ?? family) === currentCanonical

  const usedGroup: FontMenuGroup | null =
    usedFonts.length > 0
      ? {
          key: '__used__',
          label: t('editor.textBox.toolbar.usedFonts'),
          families: usedFonts,
          itemAttr: 'data-used-font',
          sectionAttr: { 'data-editor-text-used-fonts': true },
          triggerAttr: { 'data-editor-text-used-fonts-toggle': true },
          expandedAttr: { 'data-editor-text-used-fonts-expanded': true },
        }
      : null
  const regionGroups = useMemo<FontMenuGroup[]>(
    () =>
      regions.map(({ region, families: regionFamilies }) => ({
        key: region,
        label: editorTextBoxFontRegionLabel(t, region),
        families: regionFamilies,
        itemAttr: 'data-font-family',
        sectionAttr: {},
        triggerAttr: { 'data-editor-text-font-region': region },
        expandedAttr: { 'data-editor-text-font-region-expanded': region },
      })),
    [regions, t],
  )

  const [active, setActive] = useState<ActiveFlyout | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openFlyout = (group: FontMenuGroup, el: HTMLElement) => {
    clearCloseTimer()
    setActive({ ...group, rect: el.getBoundingClientRect() })
  }
  const scheduleClose = () => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => setActive(null), 160)
  }
  useEffect(() => clearCloseTimer, [])

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set())
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const mainItems = main.map((family) => (
    <FontItem
      key={family}
      family={family}
      attr="data-font-family"
      current={isCurrent(family)}
      onPick={onPick}
    />
  ))

  if (flyout) {
    const FlyoutRow = (group: FontMenuGroup) => (
      <S.MenuGroupRow
        key={group.key}
        type="button"
        aria-haspopup="menu"
        aria-expanded={active?.key === group.key}
        {...group.triggerAttr}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={(e) => openFlyout(group, e.currentTarget)}
        onMouseLeave={scheduleClose}
        onClick={(e) => openFlyout(group, e.currentTarget)}
      >
        {group.label}
        <S.MenuGroupChevron>
          <Icon icon="chevronRight" width="10px" height="10px" />
        </S.MenuGroupChevron>
      </S.MenuGroupRow>
    )

    let panelStyle: CSSProperties | undefined
    if (active && typeof window !== 'undefined') {
      const { rect } = active
      const openLeft = rect.right + FLYOUT_WIDTH > window.innerWidth
      panelStyle = {
        width: FLYOUT_WIDTH,
        left: Math.max(4, openLeft ? rect.left - FLYOUT_WIDTH : rect.right),
        top: Math.max(8, Math.min(rect.top, window.innerHeight - 340)),
      }
    }

    return (
      <>
        <S.MenuList $canvasBound $width={S.MENU_LIST_WIDTH} {...listProps}>
          {usedGroup && FlyoutRow(usedGroup)}
          {mainItems}
          {regionGroups.map(FlyoutRow)}
        </S.MenuList>
        {active &&
          typeof document !== 'undefined' &&
          createPortal(
            <S.Flyout
              data-editor-text-flyout
              style={panelStyle}
              {...active.sectionAttr}
              {...active.expandedAttr}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleClose}
            >
              {active.families.map((family) => (
                <FontItem
                  key={family}
                  family={family}
                  attr={active.itemAttr}
                  current={isCurrent(family)}
                  onPick={onPick}
                />
              ))}
            </S.Flyout>,
            document.body,
          )}
      </>
    )
  }

  const InlineGroup = (group: FontMenuGroup) => {
    const open = openGroups.has(group.key)
    return (
      <div key={group.key} {...group.sectionAttr}>
        <S.MenuGroupRow
          type="button"
          aria-expanded={open}
          {...group.triggerAttr}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggleGroup(group.key)}
        >
          {group.label}
          <S.MenuGroupChevron>
            <S.Caret $up={open}>
              <Icon icon="chevronDown" width="11px" height="6px" />
            </S.Caret>
          </S.MenuGroupChevron>
        </S.MenuGroupRow>
        <div
          {...(open ? group.expandedAttr : {})}
          aria-hidden={!open}
          hidden={!open}
        >
          {group.families.map((family) => (
            <FontItem
              key={family}
              family={family}
              attr={group.itemAttr}
              current={isCurrent(family)}
              onPick={onPick}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <S.MenuList $tall {...listProps}>
      {usedGroup && InlineGroup(usedGroup)}
      {mainItems}
      {regionGroups.map(InlineGroup)}
    </S.MenuList>
  )
}

export default EditorTextBoxFontMenu
